import React from 'react';

import { animated, useSpring, useSpringRef } from '@react-spring/web';
import { Sparkle } from 'phosphor-react';

import { usePageGenerationContext } from 'components/Pages/Admin/Page/common/AIPageGeneration/PageGenerationContext';
import Button from 'components/shared/Button';
import Text from 'components/shared/Typography/Text';

import s from './AIActions.module.css';
import { AICard } from './AICard';

export const AIActions = () => {
  const {
    cardState,
    isShowAIActions,
    onCardClear,
    onCardRate,
    onCardClose,
    onCardCancelGeneration,
    onCardGenerate,
    onCardRetry,
  } = usePageGenerationContext();

  React.useLayoutEffect(() => {
    if (!cardRef.current?.clientHeight) {
      return;
    }
    cardAnimationRef.start({ height: 'auto', immediate: true });
  }, [cardState]);

  const cardRef = React.useRef<HTMLDivElement>(null);
  const cardAnimationRef = useSpringRef();
  const launchButtonAnimationRef = useSpringRef();
  const cardAnimation = useSpring({
    ref: cardAnimationRef,
    transform: 'translateY(0px)',
    height: 'auto',
    config: { duration: 500 },
  });
  const launchButtonAnimation = useSpring({
    ref: launchButtonAnimationRef,
    transform: 'translateY(-50px)',
    config: { duration: 500 },
  });
  const hideCard = async () => {
    const newHeight = cardRef.current?.clientHeight || 0;
    cardAnimationRef.start({ height: newHeight, immediate: true });
    cardAnimationRef.start({
      transform: 'translateY(-300px)',
      height: 0,
      config: { duration: 500 },
    });
    launchButtonAnimationRef.start({
      transform: 'translateY(0px)',
      height: 0,
      onResolve: () => {
        onCardClose();
      },
    });
  };

  const showCard = async () => {
    launchButtonAnimationRef.start({ transform: 'translateY(-50px)', height: 0 });
    cardAnimationRef.start({
      height: 'auto',
      immediate: true,
      onResolve: () => {
        const newHeight = cardRef.current?.clientHeight || 0;

        cardAnimationRef.start({
          transform: 'translateY(0px)',
          height: newHeight,
          config: { duration: 500 },
        });
      },
    });
  };

  if (!isShowAIActions) {
    return null;
  }

  return (
    <div className={s.aiWrapper}>
      <animated.div style={cardAnimation} ref={cardRef}>
        <AICard
          state={cardState}
          onClose={() => {
            hideCard();
          }}
          onCancel={onCardCancelGeneration}
          onClear={onCardClear}
          onGenerate={onCardGenerate}
          onRetry={onCardRetry}
          onRate={onCardRate}
        />
      </animated.div>

      <animated.div className={s.launchButtonWrapper} style={launchButtonAnimation}>
        <Button type="empty" className={s.launchButton} onClick={showCard}>
          <Sparkle size={22} />
          <Text>Relaunch AI</Text>
        </Button>
      </animated.div>
    </div>
  );
};
